import React from 'react';
import { Box, Typography, useTheme, CircularProgress } from '@mui/material';

function ScoreGradientBar({ score, event, showValue = true, height = 24, width = '100%', displayMode = 'circular' }) {
  const theme = useTheme();
  
  // Normalize score to 0-1 range (assuming scores are typically 0-1)
  const normalizedScore = Math.min(Math.max(parseFloat(score) || 0, 0), 1);
  
  // Create gradient colors based on score with 5 distinct ranges and smooth gradients
  const getGradientColors = (score) => {
    if (score < 0.2) {
      // Very Low - Dark red gradient
      const ratio = score / 0.2;
      const red = Math.round(255 - (ratio * 40)); // 255 to 215
      const green = Math.round(71 - (ratio * 20)); // 71 to 51
      const blue = Math.round(87 - (ratio * 20)); // 87 to 67
      return [`rgb(${red}, ${green}, ${blue})`, `rgb(${red + 10}, ${green + 10}, ${blue + 10})`];
    } else if (score < 0.4) {
      // Low - Red gradient
      const ratio = (score - 0.2) / 0.2;
      const red = Math.round(215 - (ratio * 30)); // 215 to 185
      const green = Math.round(51 + (ratio * 30)); // 51 to 81
      const blue = Math.round(67 + (ratio * 30)); // 67 to 97
      return [`rgb(${red}, ${green}, ${blue})`, `rgb(${red + 10}, ${green + 10}, ${blue + 10})`];
    } else if (score < 0.6) {
      // Medium - Orange gradient
      const ratio = (score - 0.4) / 0.2;
      const red = Math.round(185 + (ratio * 70)); // 185 to 255
      const green = Math.round(81 + (ratio * 124)); // 81 to 205
      const blue = Math.round(97 + (ratio * 8)); // 97 to 105
      return [`rgb(${red}, ${green}, ${blue})`, `rgb(${red + 10}, ${green + 10}, ${blue + 10})`];
    } else if (score < 0.8) {
      // High - Yellow gradient
      const ratio = (score - 0.6) / 0.2;
      const red = Math.round(255 - (ratio * 85)); // 255 to 170
      const green = Math.round(205 + (ratio * 50)); // 205 to 255
      const blue = Math.round(105 - (ratio * 105)); // 105 to 0
      return [`rgb(${red}, ${green}, ${blue})`, `rgb(${red + 10}, ${green + 10}, ${blue + 10})`];
    } else {
      // Very High - Green gradient
      const ratio = (score - 0.8) / 0.2;
      const red = Math.round(170 - (ratio * 63)); // 170 to 107
      const green = Math.round(255 - (ratio * 28)); // 255 to 223 (a little bit more green)
      const blue = Math.round(0 + (ratio * 127)); // 0 to 127
      return [`rgb(${red}, ${green}, ${blue})`, `rgb(${red + 10}, ${green + 10}, ${blue + 10})`];
    }
  };
  
  const [color1, color2] = getGradientColors(normalizedScore);
  
  // Render different display modes
  const renderCircularMode = () => {
    const size = 40;
    const strokeWidth = 4;
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const strokeDasharray = circumference;
    const strokeDashoffset = circumference - (normalizedScore * circumference);
    
    return (
      <Box sx={{ width: width }}>
        {/* Score value in circular progress above the event label */}
        {showValue && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mb: 0.5,
            }}
          >
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
              {/* Custom SVG circle with gradient */}
              <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
                <defs>
                  <linearGradient id={`gradient-${event.replace(/\s+/g, '-')}`} x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor={color1} />
                    <stop offset="100%" stopColor={color2} />
                  </linearGradient>
                </defs>
                
                {/* Background circle */}
                <circle
                  cx={size / 2}
                  cy={size / 2}
                  r={radius}
                  fill="none"
                  stroke={theme.palette.action.hover}
                  strokeWidth={strokeWidth}
                  strokeLinecap="round"
                />
                
                {/* Progress circle with gradient - using same gradient as bars */}
                <circle
                  cx={size / 2}
                  cy={size / 2}
                  r={radius}
                  fill="none"
                  stroke={color1}
                  strokeWidth={strokeWidth}
                  strokeLinecap="round"
                  strokeDasharray={strokeDasharray}
                  strokeDashoffset={strokeDashoffset}
                  style={{
                    transition: 'stroke-dashoffset 0.3s ease'
                  }}
                />
              </svg>
              
              {/* Score text in center */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.text.primary,
                    fontWeight: 'bold',
                    fontSize: '0.7rem',
                  }}
                >
                  {parseFloat(score).toFixed(2)}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
        
        {/* Event name below the score */}
        <Box
          sx={{
            px: 1.5,
          }}
        >
          <Typography
            variant="body1"
            sx={{
              color: theme.palette.text.primary,
              fontWeight: 'medium',
              fontSize: '0.9rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              textAlign: 'center',
            }}
          >
            {event}
          </Typography>
        </Box>
      </Box>
    );
  };

  const renderBarOnlyMode = () => (
    <Box sx={{ width: width }}>
      {/* Event name above the bar */}
      <Box
        sx={{
          px: 1.5,
          mb: 0.5,
        }}
      >
        <Typography
          variant="body1"
          sx={{
            color: theme.palette.text.primary,
            fontWeight: 'medium',
            fontSize: '0.9rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            textAlign: 'center',
          }}
        >
          {event}
        </Typography>
      </Box>
      
      {/* Gradient bar container */}
      <Box
        sx={{
          width: '100%',
          height: Math.max(12, height * 0.4),
          position: 'relative',
          borderRadius: 1,
          overflow: 'hidden',
          border: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.default,
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          cursor: 'pointer',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: theme.shadows[4],
          },
        }}
      >
        {/* Background track */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: theme.palette.action.hover,
          }}
        />
        
        {/* Gradient fill */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: `${normalizedScore * 100}%`,
            height: '100%',
            background: `linear-gradient(90deg, ${color1} 0%, ${color2} 100%)`,
            transition: 'width 0.3s ease',
            boxShadow: `0 0 8px rgba(0,0,0,0.1)`,
          }}
        />
      </Box>
    </Box>
  );

  const renderBarWithScoreMode = () => (
    <Box sx={{ width: width }}>
      {/* Event label and score on the same line */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pr: 1.5,
          mb: 0.5,
        }}
      >
        {/* Event name */}
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.primary,
            fontWeight: 'medium',
            fontSize: '0.8rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1,

          }}
        >
          {event}
        </Typography>
        
        {/* Score value */}
        {showValue && (
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.primary,
              fontWeight: 'bold',
              fontSize: '0.8rem',
              ml: 1,
              minWidth: '50px',
              textAlign: 'right',
            }}
          >
            {parseFloat(score).toFixed(3)}
          </Typography>
        )}
      </Box>
      
      {/* Gradient bar container underneath */}
      <Box
        sx={{
          width: '100%',
          height: Math.max(12, height * 0.4),
          position: 'relative',
          borderRadius: 1,
          overflow: 'hidden',
          border: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.default,
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          cursor: 'pointer',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: theme.shadows[4],
          },
        }}
      >
        {/* Background track */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: theme.palette.action.hover,
          }}
        />
        
        {/* Gradient fill */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: `${normalizedScore * 100}%`,
            height: '100%',
            background: `linear-gradient(90deg, ${color1} 0%, ${color2} 100%)`,
            transition: 'width 0.3s ease',
            boxShadow: `0 0 8px rgba(0,0,0,0.1)`,
          }}
        />
      </Box>
    </Box>
  );

  // Return the appropriate mode based on displayMode prop
  switch (displayMode) {
    case 'bar-only':
      return renderBarOnlyMode();
    case 'bar-with-score':
      return renderBarWithScoreMode();
    case 'circular':
    default:
      return renderCircularMode();
  }
}

export default ScoreGradientBar; 